import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Modal } from '../Modal/Modal';
import { Table } from '../Table/Table';
import { Icons } from '../Icons/Icons';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import { ModalLoader } from '../ModalLoader';
import './CasesModal.scss';
import { leaderboardEvents, ParticipantClient } from '../../api/leaderboard-events';
import { useLoader } from '../../contexts/LoaderContext';
import { useAlert } from '../../contexts/AlertContext';
import { useActiveLeaderboardEvent } from '../../hooks/useActiveLeaderboardEvent';
import { exportApi } from '../../api/export';

interface CasesModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  participantId?: number;
  onChange?: () => void;
}

export const CasesModal: React.FC<CasesModalProps> = ({
  isOpen,
  onClose,
  userName,
  participantId,
  onChange,
}) => {
  const [cases, setCases] = useState<ParticipantClient[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = useAlert();
  const { activeEvent } = useActiveLeaderboardEvent();

  const fetchCases = useCallback(async () => {
    if (!participantId || !activeEvent?.id) return;
    try {
      const list = await leaderboardEvents.getParticipantClients(activeEvent.id, participantId);
      setCases(list);
      setHasLoaded(true);
    } catch (e) {
      showAlert('Failed to load cases', 'error');
      setHasLoaded(true);
    }
  }, [participantId, showAlert, activeEvent?.id]);

  useEffect(() => {
    if (isOpen) {
      setHasLoaded(false);
      fetchCases();
    } else if (!isOpen) {
      setHasLoaded(false);
      setCases([]);
    }
  }, [isOpen, participantId, fetchCases]);
  const { isDeleteModalOpen, openDeleteModal, closeDeleteModal, handleConfirmDelete } =
    useDeleteConfirmation(async (id) => {
      if (!participantId || !id || !activeEvent?.id) return;
      try {
        showLoader();
        await leaderboardEvents.deleteParticipantClient(activeEvent.id, participantId, id);
        closeDeleteModal();
        await fetchCases();
        if (onChange) onChange();
        showAlert('Client deleted', 'success');
      } catch (e) {
        showAlert('Failed to delete client', 'error');
      } finally {
        hideLoader();
      }
    });

  const columns = [
    { id: '#', label: '#', width: '5%' },
    { id: 'clientName', label: "Client's Name", width: '15%' },
    { id: 'clientId', label: "Client's ID", width: '12%' },
    { id: 'hours', label: 'Hours', width: '8%' },
    { id: 'area', label: 'Area', width: '12%' },
    { id: 'office', label: 'Office', width: '12%' },
    { id: 'agency', label: 'Agency', width: '12%' },
    { id: 'referralDate', label: 'Referral Date', width: '12%' },
    { id: 'socDate', label: 'SOC Date', width: '12%' },
    {
      id: 'action',
      label: 'Action',
      width: '10%',
      render: (row: { id: number }) => (
        <div className="action-icons">
          <span className="delete-icon" onClick={() => openDeleteModal(row.id)}>
            {Icons.Delete}
          </span>
        </div>
      ),
    },
  ];

  const data = useMemo(
    () =>
      cases.map((c, index) => ({
        id: c.id,
        '#': index + 1,
        clientName: [c.firstName, c.lastName].filter(Boolean).join(' ') || '-',
        clientId: c.clientId ?? '-',
        hours: c.hours ?? 0,
        area: c.districtName ?? '-',
        office: c.officeName ?? '-',
        agency: c.agencyName ?? '-',
        referralDate: c.referralDate ? new Date(c.referralDate).toLocaleDateString() : '-',
        socDate: c.startOfCareDate ? new Date(c.startOfCareDate).toLocaleDateString() : '-',
      })),
    [cases]
  );

  const handleExportClients = async () => {
    try {
      if (!participantId || !activeEvent?.id) return;

      const result = await exportApi.participantClients(activeEvent.id, participantId);

      const sanitizedUserName = userName.replace(/[^a-zA-Z0-9]/g, '_');
      const customFilename = result.filename.includes('.')
        ? result.filename.replace(/(\.[^.]+)$/, `_${sanitizedUserName}$1`)
        : `${result.filename}_${sanitizedUserName}.xlsx`;

      const url = window.URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = customFilename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showAlert('Export completed successfully', 'success');
    } catch (error) {
      showAlert('Failed to export clients', 'error');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="cases-modal">
        <div className="cases-modal-header">
          <h2>{userName}'s Cases</h2>
          <button className="btn outline export-button" onClick={handleExportClients}>
            Export
          </button>
        </div>
        {!hasLoaded ? (
          <ModalLoader />
        ) : data.length === 0 ? (
          <div className="no-data-message">No cases found</div>
        ) : (
          <Table columns={columns} data={data} />
        )}
      </div>
      <Modal isOpen={isDeleteModalOpen} onClose={closeDeleteModal}>
        <ConfirmationModal
          onCancel={closeDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Are you sure you want to delete this client?"
          message="This action cannot be undone. Once deleted, these items will be permanently removed."
        />
      </Modal>
    </Modal>
  );
};
